import { createContext } from 'react';
import { z } from 'zod';
import { useUpload } from '../../csv-importer/use-upload';
import { useCreateSynonym } from 'shared/hooks/search-synonyms.hooks';

export const UploadContext = createContext();

// Column key → human label shown in the mapping/validation steps.
const propertyOptions = {
  mappingType: 'Mapping Type',
  synonyms: 'Synonyms (comma-separated)',
  input: 'Input (comma-separated, explicit only)',
  note: 'Note',
};

// Split a comma-separated cell into trimmed, non-empty terms.
const splitTerms = value =>
  (value || '')
    .split(',')
    .map(term => term.trim())
    .filter(Boolean);

// `mappingType` is the only field with a fixed option set — it renders as a
// select in the validation grid. `synonyms`/`input` are free-form term lists, so
// they stay plain text inputs (comma-separated) and are split in the schema
// transform below; using `multiple` here would drop values not in a fixed list.
const synonymsPropertiesMap = {
  mappingType: {
    // Normalize case/whitespace from the CSV so "Equivalent" maps to the enum.
    findMatch: value => {
      const normalized = (value || '').trim().toLowerCase();
      return normalized === 'explicit' || normalized === 'equivalent' ? normalized : value;
    },
    options: [
      { key: 'equivalent', value: 'Equivalent' },
      { key: 'explicit', value: 'Explicit' },
    ],
  },
};

// Mirrors the single-record SYNONYM_SCHEMA and the backend Joi rules: synonyms
// ≥1; input required ≥1 for explicit, forced empty for equivalent. Validates the
// raw CSV strings, then transforms comma-separated cells into string arrays.
const validationSchema = z.object({
  synonyms: z.array(
    z
      .object({
        mappingType: z.enum(['equivalent', 'explicit'], {
          errorMap: () => ({ message: 'Must be "equivalent" or "explicit"' }),
        }),
        synonyms: z.string(),
        input: z.string().optional(),
        note: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (splitTerms(data.synonyms).length < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['synonyms'],
            message: 'Add at least one synonym',
          });
        }
        if (data.mappingType === 'explicit' && splitTerms(data.input).length < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['input'],
            message: 'Explicit mappings need at least one input term',
          });
        }
      })
      .transform(data => ({
        mappingType: data.mappingType,
        synonyms: splitTerms(data.synonyms),
        input: data.mappingType === 'explicit' ? splitTerms(data.input) : [],
        note: (data.note || '').trim(),
      })),
  ),
});

export const UploadSynonymsProvider = ({ children }) => {
  const { mutateAsync: createSynonym } = useCreateSynonym();
  const { showResults, ...rest } = useUpload({ propertiesMap: synonymsPropertiesMap });

  const onSubmit = async data => {
    const rows = data.synonyms;

    // No bulk endpoint — one POST per row, tolerating partial failures. The
    // mutation hook invalidates the synonyms cache on each success.
    const settled = await Promise.allSettled(
      rows.map(row =>
        createSynonym({
          mappingType: row.mappingType,
          synonyms: row.synonyms,
          input: row.input,
          note: row.note,
        }),
      ),
    );

    const created = [];
    const failed = [];
    settled.forEach((result, index) => {
      const label = rows[index].synonyms.join(', ') || rows[index].input.join(', ');
      if (result.status === 'fulfilled') {
        created.push({ email: label, message: 'Created successfully' });
      } else {
        const err = result.reason;
        failed.push({
          email: label,
          message: err?.response?.data?.message || err?.message || 'Failed to create',
        });
      }
    });

    showResults({ new: created, updated: [], failed });
  };

  return (
    <UploadContext.Provider
      value={{
        ...rest,
        showResults,
        validationSchema,
        propertyOptions,
        onSubmit,
        mandatoryFields: ['mappingType', 'synonyms'],
        CustomFileUploadRender: () => (
          <div className="mb-4">
            <label className="text-sm text-gray-500">
              Upload a CSV file with the following columns:
            </label>
            <ul className="list-disc pl-5 text-sm text-gray-700">
              <li>Mapping Type (equivalent or explicit)</li>
              <li>Synonyms (comma-separated, at least one)</li>
              <li>Input (comma-separated — required for explicit, leave empty for equivalent)</li>
              <li>Note (optional label, e.g. specialty)</li>
            </ul>
            <p className="text-xs text-gray-400 mt-2">
              Mappings are global — they apply across all organizations.
            </p>
          </div>
        ),
      }}>
      {children}
    </UploadContext.Provider>
  );
};
